import { ResponsiveLine, Serie } from '@nivo/line';
import dayjs from 'dayjs';
import React from 'react';
import { groupBy } from '../../../components/OrderListTable/utils/groupBy';
import { PerformanceScanByTime } from '../../../fetch/fetchScanPerformanceByTime';
import { PerformanceScanByTimePerStore } from '../../../fetch/fetchScanPerformanceByTimePerStore';

declare interface xyFormat {
  x: string;
  y: number;
}

type VizualizationScansFormatedProps = {
  formatedData: {
    id: string;
    data: {
      x: string;
      y: number;
    }[];
  }[];
  legendY: string;
};

function VizualizationScansFormated({
  formatedData,
  legendY = 'legend y' /* see data tab */,
}: VizualizationScansFormatedProps): JSX.Element {
  return (
    <div style={{ height: '500px', width: '90vw' }}>
      <ResponsiveLine
        data={formatedData as Serie[]}
        margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
        xScale={{
          type: 'time',
          format: '%H:%M',
          precision: 'minute',
        }}
        xFormat="time:%H:%M"
        yScale={{ type: 'linear', stacked: false, min: 0, max: 'auto' }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          orient: 'bottom',
          format: '%H:%M',
          tickSize: 5,
          tickPadding: 5,
          tickRotation: -60,
          legend: 'time of scans',
          legendOffset: 45,
          legendPosition: 'middle',
        }}
        axisLeft={{
          orient: 'left',
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: legendY,
          legendOffset: -40,
          legendPosition: 'middle',
        }}
        colors={{ scheme: 'paired' }}
        curve="monotoneX"
        pointSize={10}
        pointColor={{ theme: 'background' }}
        pointBorderWidth={3}
        pointBorderColor={{ from: 'serieColor' }}
        pointLabel="y"
        enablePointLabel={false}
        pointLabelYOffset={-12}
        useMesh={true}
        legends={[
          {
            anchor: 'bottom-right',
            direction: 'column',
            justify: false,
            translateX: 300,
            translateY: 0,
            itemsSpacing: 0,
            itemDirection: 'left-to-right',
            itemWidth: 300,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: 'circle',
            symbolBorderColor: 'rgba(0, 0, 0, .5)',
            effects: [
              {
                on: 'hover',
                style: {
                  itemBackground: 'rgba(0, 0, 0, .03)',
                  itemOpacity: 1,
                },
              },
            ],
          },
        ]}
      />
    </div>
  );
}

function constructXYData(timePart: string, y: number): xyFormat {
  return { x: dayjs(timePart).format('HH:mm'), y };
}

function getXYFormatedDataCompletedScans(obj: PerformanceScanByTime): xyFormat {
  return constructXYData(obj.timePart, obj.nrOfCompletedScans);
}

function getXYFormatedDataPresortScans(obj: PerformanceScanByTime): xyFormat {
  const y = obj.nrOfFirstScans - obj.nrOfCompletedScans;
  return constructXYData(obj.timePart, y);
}

function emptyXyFormat(timeString: string): xyFormat {
  return constructXYData(timeString, 0);
}

function getDataToShowTL(dataToShow: PerformanceScanByTime[]) {
  return dataToShow.map(obj => obj.timePart);
}

function getForDateOrdered(
  dataToShow: PerformanceScanByTime[],
  timeString: string
): PerformanceScanByTime {
  const cond = (timePart: string) => dayjs(timePart).isSame(dayjs(timeString));
  const filteredArray = dataToShow.filter(obj => cond(obj.timePart));

  return filteredArray[0];
}

function nrOfCageScans5Min(
  dataToShow: PerformanceScanByTime[],
  timeList: string[]
): xyFormat[] {
  const dataToShowTL = getDataToShowTL(dataToShow);
  const formatedByDateList = timeList.map((timeString: string) => {
    if (dataToShowTL.indexOf(timeString) === -1)
      return emptyXyFormat(timeString);

    const forDateOrdered = getForDateOrdered(dataToShow, timeString);
    return getXYFormatedDataCompletedScans(forDateOrdered);
  });

  return formatedByDateList;
}

function nrOfPresortScansPer5Min(
  dataToShow: PerformanceScanByTime[],
  timeList: string[]
) {
  const dataToShowTL = getDataToShowTL(dataToShow);
  const formatedByDateList = timeList.map((timeString: string) => {
    if (dataToShowTL.indexOf(timeString) === -1)
      return emptyXyFormat(timeString);

    const forDateOrdered = getForDateOrdered(dataToShow, timeString);
    return getXYFormatedDataPresortScans(forDateOrdered);
  });

  return formatedByDateList;
}

function mapScans(scannerList: any, dataFn: any, timeList: string[]) {
  const cageScans = scannerList.map(obj => {
    return {
      id: `${obj[1][0].firstName} ${obj[1][0].lastName}`,
      data: dataFn(obj[1], timeList),
    };
  });
  return cageScans;
}

function getCageScans(groupedByScannerList, timeList: string[]) {
  return mapScans(groupedByScannerList, nrOfCageScans5Min, timeList);
}

function getPreSortScans(groupedByScannerList, timeList: string[]) {
  return mapScans(groupedByScannerList, nrOfPresortScansPer5Min, timeList);
}

export function getSenderNameFromEmail(emailString: string): string {
  if (!emailString) return 'unknown';

  const splitString = emailString.split('@');
  const fromPart = splitString.length >= 2 ? splitString[1] : null;
  return fromPart ? fromPart.split('.')[0] : 'unknown';
}

function getSenderEmailScans(groupedByScannerList, timeList: string[]) {
  const cageScans: {
    id: string;
    data: xyFormat[];
  }[] = groupedByScannerList.map(obj => {
    return {
      id: `${obj[1][0].senderEmail}`,
      data: nrOfCageScans5Min(obj[1], timeList),
    };
  });

  return cageScans;
}

export function VizualizationScanPerformanceByTimeList({
  dataToShow,
}: {
  dataToShow: PerformanceScanByTime[];
}): JSX.Element {
  const groupedByScanner = groupBy(dataToShow, 'scannedBy');
  const groupedByScannerList: [string, PerformanceScanByTime[]][] =
    Object.entries(groupedByScanner);
  const groupedByTime = groupBy(dataToShow, 'timePart');
  const timeList = Object.keys(groupedByTime);
  const cageScans = getCageScans(groupedByScannerList, timeList);
  const preSortScans = getPreSortScans(groupedByScannerList, timeList);

  return (
    <>
      <VizualizationScansFormated
        formatedData={cageScans}
        legendY={'Cage scan per 5 min'}
      />

      <VizualizationScansFormated
        formatedData={preSortScans}
        legendY={'Pre scan per 5 min'}
      />
    </>
  );
}

export function VizualizationScanPerformanceByTimePerStoreList({
  dataToShow,
}: {
  dataToShow: PerformanceScanByTimePerStore[];
}): JSX.Element {
  const uniqueSenders: PerformanceScanByTimePerStore[] = dataToShow.map(obj => {
    const fromSenderName: string = getSenderNameFromEmail(obj.senderEmail);

    return {
      senderEmail: `${fromSenderName}`,
      timePart: obj.timePart,
      nrOfFirstScans: obj.nrOfFirstScans,
      totalNrOfScans: obj.totalNrOfScans,
      nrOfCompletedScans: obj.nrOfCompletedScans,
      firstScan: obj.firstScan,
    };
  });
  const groupedBySenderEmail = groupBy(uniqueSenders, 'senderEmail');
  const groupedBySenderEmailList: [string, PerformanceScanByTime[]][] =
    Object.entries(groupedBySenderEmail);
  const groupedByTime = groupBy(dataToShow, 'timePart');
  const timeList = Object.keys(groupedByTime);
  const shopScans = getSenderEmailScans(groupedBySenderEmailList, timeList);

  return (
    <>
      <VizualizationScansFormated
        formatedData={shopScans}
        legendY={'Shop scan per 5 min'}
      />
    </>
  );
}
